// ============================================================
// relativeTime.ts
// Converts a Date or ISO string into a human-readable relative
// time string, e.g. "2 hours ago", "yesterday", "3 days ago".
// No external dependencies — uses simple arithmetic.
// ============================================================

/**
 * Returns a human-friendly relative time string.
 * @param input - Date object or ISO 8601 date string
 */
export const relativeTime = (input: Date | string): string => {
  const date = typeof input === 'string' ? new Date(input) : input
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()

  // Guard against future dates — treat as "Just now" rather than going negative
  if (diffMs < 0) return 'Just now'

  const seconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)

  // Sub-minute: ambiguous to label with an exact count, so soften.
  if (seconds < 60) return 'Just now'
  // 1 to 4 minutes: a softer phrase reads better than "1 minute ago" /
  // "2 minutes ago" for very recent activity.
  if (minutes < 5) return 'A few minutes ago'
  // 5 to 59 minutes: precise count. The single-minute case can no longer
  // occur here, so the old `minutes === 1` branch is intentionally dropped.
  if (minutes < 60) return `${minutes} minutes ago`
  if (hours === 1) return '1 hour ago'
  if (hours < 24) return `${hours} hours ago`
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  if (weeks === 1) return '1 week ago'
  if (weeks < 5) return `${weeks} weeks ago`
  if (months === 1) return '1 month ago'
  if (months < 12) return `${months} months ago`

  return 'over a year ago'
}
